// Журнал обмена с 1С на странице «Интеграции».
//
// Зачем отдельным блоком в кабинете: на стороне 1С тело ответа обычно никуда
// не сохраняется, поэтому разбор «у нас не грузится» упирался в логи сервера.
// Здесь интегратор сам видит свой запрос и наш отказ с причиной.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  Copy,
  RefreshCw,
  ScrollText,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { listErpLogs, type ErpLog } from '@/services/integrations';
import { Spinner } from '@/components/fields';
import { dateTimeExact } from '@/lib/format';

const METHOD_COLOR: Record<string, string> = {
  GET: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  POST: 'bg-orange/10 text-orange-700 border-orange/30',
  PATCH: 'bg-sky-50 text-sky-700 border-sky-200',
  DELETE: 'bg-red-50 text-red-700 border-red-200',
};

/** 2xx/304 — норма, 4xx — виновата присланная 1С, 5xx — наша сторона. */
function statusTone(status: number): string {
  if (status >= 500) return 'bg-red-100 text-red-800';
  if (status >= 400) return 'bg-amber-100 text-amber-800';
  return 'bg-emerald-50 text-emerald-700';
}

function copy(text: string) {
  navigator.clipboard
    ?.writeText(text)
    .then(() => toast.success('Скопировано'))
    .catch(() => toast.error('Не удалось скопировать'));
}

/** Показываем JSON с отступами, если это JSON; иначе — как пришло. */
function pretty(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function Body({ title, raw, truncated }: { title: string; raw: string; truncated: boolean }) {
  const text = pretty(raw);
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="label mb-0">{title}</span>
        <button className="btn-ghost py-0.5 px-1.5" onClick={() => copy(text)} title="Скопировать">
          <Copy size={13} />
        </button>
      </div>
      <pre className="max-h-72 overflow-auto rounded-lg bg-ink/[0.04] border border-line px-3 py-2 text-[11px] leading-relaxed text-ink/80 whitespace-pre-wrap break-all">
        {text}
      </pre>
      {truncated && (
        <p className="text-[11px] text-ink/40 mt-1">
          Показано начало — длинные выгрузки в журнале обрезаются.
        </p>
      )}
    </div>
  );
}

function Row({ log }: { log: ErpLog }) {
  const [open, setOpen] = useState(false);
  const hasDetails = Boolean(log.requestBody || log.responseBody);

  return (
    <div className="py-2">
      <button
        className={`w-full flex items-center gap-2 text-left ${hasDetails ? '' : 'cursor-default'}`}
        onClick={() => hasDetails && setOpen((v) => !v)}
      >
        <span
          className={`inline-block w-[52px] text-center text-[10px] font-bold uppercase rounded border px-1 py-0.5 shrink-0 ${
            METHOD_COLOR[log.method] ?? 'bg-ink/5 text-ink/60 border-line'
          }`}
        >
          {log.method}
        </span>
        <code className="text-xs text-ink/80 shrink-0">{log.path}</code>
        <span className={`badge shrink-0 ${statusTone(log.status)}`}>{log.status}</span>
        {log.repeatCount > 1 && (
          <span className="badge bg-ink/5 text-ink/45 shrink-0" title="Одинаковых запросов подряд">
            ×{log.repeatCount}
          </span>
        )}
        <span className="text-[11px] text-ink/40 ml-auto shrink-0 tabular-nums">
          {dateTimeExact(log.createdAt)} · {log.durationMs} мс
        </span>
        {hasDetails && (
          <ChevronDown
            size={14}
            className={`text-ink/35 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {open && (
        <div className="mt-2.5 ml-[60px] space-y-2.5">
          {log.query && (
            <div className="text-[11px] text-ink/50">
              Параметры: <code className="text-ink/70 break-all">{log.query}</code>
            </div>
          )}
          {log.requestBody && (
            <Body title="Прислала 1С" raw={log.requestBody} truncated={log.truncated} />
          )}
          {log.responseBody && (
            <Body title="Ответил Resstro" raw={log.responseBody} truncated={log.truncated} />
          )}
          <div className="text-[11px] text-ink/35">
            {log.keyName ? `ключ «${log.keyName}»` : 'ключ удалён'}
            {log.repeatCount > 1 && ` · первый из серии ${dateTimeExact(log.firstAt)}`}
            {log.ip ? ` · IP ${log.ip}` : ''}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ErpLogPanel() {
  const [onlyProblems, setOnlyProblems] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['erp-logs', onlyProblems],
    queryFn: () => listErpLogs({ onlyProblems }),
    refetchInterval: 15_000,
  });

  const logs = data?.results ?? [];
  const problems = data?.problemsLast24h ?? 0;

  return (
    <section className="card p-5">
      <div className="flex items-start gap-3.5">
        <div className="w-9 h-9 rounded-lg bg-ink/5 text-ink/50 flex items-center justify-center shrink-0">
          <ScrollText size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-ink">Журнал обмена с 1С</h3>
            {problems > 0 && (
              <span className="badge bg-amber-100 text-amber-800">
                <AlertTriangle size={11} /> отказов за сутки: {problems}
              </span>
            )}
            <div className="ml-auto flex items-center gap-1">
              <button
                className={`btn-ghost text-xs ${onlyProblems ? 'text-amber-700 bg-amber-50' : ''}`}
                onClick={() => setOnlyProblems((v) => !v)}
              >
                {onlyProblems ? 'Показать все' : 'Только отказы'}
              </button>
              <button className="btn-ghost" onClick={() => refetch()} title="Обновить">
                <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
          <p className="text-sm text-ink/55 mt-0.5">
            Что ваша программа присылала и что мы ответили. Разверните строку — увидите тело
            запроса и причину отказа. Это то, что нужно показать специалисту по 1С, когда обмен
            «не грузится». Журнал хранится 14 дней.
          </p>

          <div className="mt-4">
            {isLoading ? (
              <Spinner label="Загрузка…" />
            ) : logs.length === 0 ? (
              <p className="text-sm text-ink/45">
                {onlyProblems
                  ? 'Отказов нет — обмен идёт чисто.'
                  : 'Запросов пока не было. Здесь появятся обращения вашей программы, как только она начнёт обмен.'}
              </p>
            ) : (
              <div className="divide-y divide-line border-t border-line">
                {logs.map((log) => (
                  <Row key={log.id} log={log} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
